/**
 * Calls to the ingestion server (worker/). Every call carries the Supabase access
 * token as a Bearer header; the server verifies it and maps to the valuer.
 */
import { supabase } from './supabase';
import { env } from './env';

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  constructor(code: string, message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
  }
}

export interface Profile {
  isValuer: boolean;
  role: string | null;
  /** app_metadata.is_admin === true (Phase 6). Independent of role - see ADMIN.md. */
  isAdmin: boolean;
  valuerId: string | null;
  displayName: string | null;
  email: string | null;
}

export interface UploadListItem {
  id: string;
  address: string | null;
  status: string;
  date: string | null;
  neededByAt: string | null;
}

export interface UploadResult {
  reportId: string;
  status: 'queued' | 'duplicate';
  duplicate: boolean;
  address: string;
}

async function accessToken(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) {
    throw new ApiError('session_expired', 'Your session has expired. Please sign in again.', 401);
  }
  return token;
}

async function getJson<T>(path: string): Promise<T> {
  const token = await accessToken();
  let res: Response;
  try {
    res = await fetch(`${env.ingestBaseUrl}${path}`, {
      headers: { authorization: `Bearer ${token}` },
    });
  } catch {
    throw new ApiError('network', 'Couldn’t reach the server. Check your connection and try again.', 0);
  }
  const body = (await res.json().catch(() => null)) as { error?: { code: string; message: string } } | null;
  if (!res.ok) {
    throw new ApiError(body?.error?.code ?? 'server_error', body?.error?.message ?? 'Something went wrong.', res.status);
  }
  return body as T;
}

async function sendJson<T>(
  method: 'POST' | 'PATCH' | 'PUT',
  path: string,
  payload: unknown,
  opts?: { keepalive?: boolean },
): Promise<T> {
  const token = await accessToken();
  let res: Response;
  try {
    res = await fetch(`${env.ingestBaseUrl}${path}`, {
      method,
      keepalive: opts?.keepalive,
      headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch {
    throw new ApiError('network', 'Couldn’t reach the server. Check your connection and try again.', 0);
  }
  const body = (await res.json().catch(() => null)) as { error?: { code: string; message: string } } | null;
  if (!res.ok) {
    throw new ApiError(body?.error?.code ?? 'server_error', body?.error?.message ?? 'Something went wrong.', res.status);
  }
  return body as T;
}

async function postJson<T>(path: string, payload: unknown, opts?: { keepalive?: boolean }): Promise<T> {
  return sendJson<T>('POST', path, payload, opts);
}

async function patchJson<T>(path: string, payload: unknown): Promise<T> {
  return sendJson<T>('PATCH', path, payload);
}

async function putJson<T>(path: string, payload: unknown): Promise<T> {
  return sendJson<T>('PUT', path, payload);
}

async function deleteJson<T>(path: string): Promise<T> {
  const token = await accessToken();
  let res: Response;
  try {
    res = await fetch(`${env.ingestBaseUrl}${path}`, {
      method: 'DELETE',
      headers: { authorization: `Bearer ${token}` },
    });
  } catch {
    throw new ApiError('network', 'Couldn’t reach the server. Check your connection and try again.', 0);
  }
  const body = (await res.json().catch(() => null)) as { error?: { code: string; message: string } } | null;
  if (!res.ok) {
    throw new ApiError(body?.error?.code ?? 'server_error', body?.error?.message ?? 'Something went wrong.', res.status);
  }
  return body as T;
}

export function fetchProfile(): Promise<Profile> {
  return getJson<Profile>('/api/me');
}

export async function fetchMyUploads(): Promise<UploadListItem[]> {
  const data = await getJson<{ uploads: UploadListItem[] }>('/api/my-uploads');
  return data.uploads;
}

/** Part IX: rolling processing-time estimate (see lib/eta.ts for phrasing helpers). */
export async function fetchEta(): Promise<import('./lib/eta').EtaInfo> {
  return getJson<import('./lib/eta').EtaInfo>('/api/eta');
}

export interface UploadArgs {
  address: string;
  report: File;
  letterOfInstruction: File;
  /** Optional "needed by" deadline, ISO date/time string. Omit or pass null if not set. */
  neededByAt?: string | null;
  /** Optional free-text note for the reviewer, shown above the report summary. Omit or pass null if not set. */
  notes?: string | null;
  onProgress?: (percent: number) => void;
}

/** POST the two files. Uses XHR so we can report real upload progress. */
export async function uploadReport(args: UploadArgs): Promise<UploadResult> {
  const token = await accessToken();
  const form = new FormData();
  form.append('address', args.address);
  if (args.neededByAt) form.append('needed_by_at', args.neededByAt);
  if (args.notes) form.append('valuer_notes', args.notes);
  form.append('report', args.report, args.report.name);
  form.append('letter_of_instruction', args.letterOfInstruction, args.letterOfInstruction.name);

  return new Promise<UploadResult>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${env.ingestBaseUrl}/api/uploads`);
    xhr.setRequestHeader('authorization', `Bearer ${token}`);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && args.onProgress) {
        args.onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onload = () => {
      let body: { error?: { code: string; message: string } } & Partial<UploadResult>;
      try {
        body = JSON.parse(xhr.responseText);
      } catch {
        reject(new ApiError('server_error', 'Unexpected response from the server.', xhr.status));
        return;
      }
      if (xhr.status >= 200 && xhr.status < 300 && body.reportId) {
        resolve(body as UploadResult);
      } else {
        reject(new ApiError(body.error?.code ?? 'server_error', body.error?.message ?? 'Upload failed.', xhr.status));
      }
    };
    xhr.onerror = () =>
      reject(new ApiError('network', 'The upload didn’t go through. Check your connection and try again.', 0));
    xhr.send(form);
  });
}

// --- Reviewer actions (Phase 3) ------------------------------------------------
// Reviewer READS (valuers/reports/check_results/risk_results/error_findings) go straight
// to Supabase — see reviewer/queries.ts. These three calls are the only things that still
// need the service role: signed URLs for the private documents bucket, and writes that
// should be audited (status transitions, the reviewer's note).

export interface DocumentUrls {
  report: string | null;
  letterOfInstruction: string | null;
  expiresInSeconds: number;
}

export function fetchDocumentUrls(reportId: string): Promise<DocumentUrls> {
  return getJson<DocumentUrls>(`/api/reviewer/reports/${reportId}/documents`);
}

export type ReviewerStatus = 'reviewing' | 'checked' | 'done';

export function setReportStatus(
  reportId: string,
  status: ReviewerStatus,
  opts?: { keepalive?: boolean },
): Promise<{ status: string }> {
  return postJson<{ status: string }>(`/api/reviewer/reports/${reportId}/status`, { status }, opts);
}

export function setReviewerNote(reportId: string, note: string): Promise<{ ok: true }> {
  return postJson<{ ok: true }>(`/api/reviewer/reports/${reportId}/note`, { note });
}

// --- Admin actions (Phase 6) ---------------------------------------------------
// Admin READS (check_schema/risk_matrix_schema/agent_config/standing_instructions/banks/
// cron_settings/audit_log, including inactive rows) go straight to Supabase - see
// admin/queries.ts - now that RLS grants is_admin() SELECT. These calls are only the
// writes, which stay worker-mediated + audited (see ADMIN.md).

export interface CheckSchemaInput {
  checkKey: string;
  category: string;
  label: string;
  description: string | null;
  displayOrder: number;
  handlerType: 'static' | 'agentic';
  bankId: string | null;
  active: boolean;
}

export function createCheckSchemaRow(input: CheckSchemaInput) {
  return postJson('/api/admin/check-schema', input);
}
export function updateCheckSchemaRow(checkKey: string, patch: Partial<Omit<CheckSchemaInput, 'checkKey'>>) {
  return patchJson(`/api/admin/check-schema/${encodeURIComponent(checkKey)}`, patch);
}
export function reorderCheckSchema(items: Array<{ checkKey: string; displayOrder: number }>) {
  return postJson('/api/admin/check-schema/reorder', { items });
}

export interface RiskSchemaInput {
  riskKey: string;
  riskGroup: 'property' | 'market';
  label: string;
  description: string | null;
  displayOrder: number;
  commentRequiredAbove: number;
  active: boolean;
}

export function createRiskSchemaRow(input: RiskSchemaInput) {
  return postJson('/api/admin/risk-schema', input);
}
export function updateRiskSchemaRow(riskKey: string, patch: Partial<Omit<RiskSchemaInput, 'riskKey'>>) {
  return patchJson(`/api/admin/risk-schema/${encodeURIComponent(riskKey)}`, patch);
}
export function reorderRiskSchema(items: Array<{ riskKey: string; displayOrder: number }>) {
  return postJson('/api/admin/risk-schema/reorder', { items });
}

export function updateAgentConfig(key: string, value: Record<string, unknown>) {
  return putJson(`/api/admin/agent-config/${encodeURIComponent(key)}`, { value });
}

export interface NewStandingInstructionInput {
  bankId: string | null;
  isInstituteBaseline: boolean;
  version: string;
  body: string;
  effectiveFrom: string | null;
}

export function createStandingInstructionVersion(input: NewStandingInstructionInput) {
  return postJson('/api/admin/standing-instructions', input);
}
export function updateStandingInstruction(id: string, patch: { body?: string; version?: string; effectiveFrom?: string | null }) {
  return patchJson(`/api/admin/standing-instructions/${id}`, patch);
}
export function activateStandingInstruction(id: string) {
  return postJson<{ ok: true }>(`/api/admin/standing-instructions/${id}/activate`, {});
}
export function deactivateStandingInstruction(id: string) {
  return postJson<{ ok: true }>(`/api/admin/standing-instructions/${id}/deactivate`, {});
}

/** Upload a standing-instructions PDF for Claude-assisted extraction into an editable draft. */
export async function importStandingInstructionPdf(args: {
  bankId: string | null;
  isInstituteBaseline: boolean;
  file: File;
}): Promise<{ id: string; body: string; version: string }> {
  const token = await accessToken();
  const form = new FormData();
  form.append('isInstituteBaseline', String(args.isInstituteBaseline));
  if (args.bankId) form.append('bankId', args.bankId);
  form.append('file', args.file, args.file.name);

  let res: Response;
  try {
    res = await fetch(`${env.ingestBaseUrl}/api/admin/standing-instructions/import`, {
      method: 'POST',
      headers: { authorization: `Bearer ${token}` },
      body: form,
    });
  } catch {
    throw new ApiError('network', 'Couldn’t reach the server. Check your connection and try again.', 0);
  }
  const body = (await res.json().catch(() => null)) as
    | ({ error?: { code: string; message: string } } & Partial<{ id: string; body: string; version: string }>)
    | null;
  if (!res.ok || !body?.id) {
    throw new ApiError(body?.error?.code ?? 'server_error', body?.error?.message ?? 'Could not import that PDF.', res.status);
  }
  return body as { id: string; body: string; version: string };
}

export function createBank(name: string, aliases: string[]) {
  return postJson('/api/admin/banks', { name, aliases });
}
export function updateBank(id: string, patch: { name?: string; aliases?: string[] }) {
  return patchJson(`/api/admin/banks/${id}`, patch);
}

export function updateMorningSummarySchedule(localTime: string) {
  return putJson<{ cron: string; label: string }>('/api/admin/cron/morning-summary', { localTime });
}

export function rerunCompliance(reportId: string) {
  return postJson<{ jobId: string }>(`/api/admin/reports/${reportId}/rerun`, {});
}

export function rollbackAuditEntry(auditLogId: string) {
  return postJson<{ ok: true }>(`/api/admin/audit-log/${auditLogId}/rollback`, {});
}

// --- Markup + feedback (Phase 7) ------------------------------------------------
// Reviewer/admin reads (report_annotations, feedback_dispatches) go straight to Supabase -
// see reviewer/queries.ts, now that RLS grants is_reviewer()/is_admin() SELECT. These calls
// are the writes, which stay worker-mediated + audited, same as every other write.

export interface AnnotationInput {
  page: number;
  type: 'highlight' | 'note' | 'shape';
  coordinates: unknown;
  text?: string | null;
}

export interface AnnotationResult {
  id: string;
  report_id: string;
  page: number;
  type: 'highlight' | 'note' | 'shape';
  coordinates: unknown;
  color: string;
  text: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export function createAnnotation(reportId: string, input: AnnotationInput) {
  return postJson<AnnotationResult>(`/api/reviewer/reports/${reportId}/annotations`, input);
}

export function updateAnnotation(reportId: string, annotationId: string, patch: { coordinates?: unknown; text?: string | null }) {
  return patchJson<AnnotationResult>(`/api/reviewer/reports/${reportId}/annotations/${annotationId}`, patch);
}

export function deleteAnnotation(reportId: string, annotationId: string) {
  return deleteJson<{ ok: true }>(`/api/reviewer/reports/${reportId}/annotations/${annotationId}`);
}

export interface SendFeedbackResult {
  dispatchId: string;
  itemCount: number;
  attached: boolean;
}

export function sendFeedbackToValuer(reportId: string) {
  return postJson<SendFeedbackResult>(`/api/reviewer/reports/${reportId}/send-feedback`, {});
}

// --- Valuer feedback screen (Phase 7) --------------------------------------------

export function fetchFeedbackDocumentUrl(reportId: string): Promise<{ url: string }> {
  return getJson<{ url: string }>(`/api/feedback/reports/${reportId}/document-url`);
}

export function markFeedbackViewed(reportId: string): Promise<{ ok: true }> {
  return postJson<{ ok: true }>(`/api/feedback/reports/${reportId}/viewed`, {});
}

export interface ResubmitResult {
  reportId: string;
  revisionNumber: number;
}

/** Resubmit a revised report against an existing (changes_requested) request. Mirrors uploadReport but with no address field - the address is inherited from the original. */
export async function resubmitReport(args: {
  reportId: string;
  report: File;
  letterOfInstruction: File;
  onProgress?: (percent: number) => void;
}): Promise<ResubmitResult> {
  const token = await accessToken();
  const form = new FormData();
  form.append('reportId', args.reportId);
  form.append('report', args.report, args.report.name);
  form.append('letter_of_instruction', args.letterOfInstruction, args.letterOfInstruction.name);

  return new Promise<ResubmitResult>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${env.ingestBaseUrl}/api/uploads/resubmit`);
    xhr.setRequestHeader('authorization', `Bearer ${token}`);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && args.onProgress) {
        args.onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onload = () => {
      let body: { error?: { code: string; message: string } } & Partial<ResubmitResult>;
      try {
        body = JSON.parse(xhr.responseText);
      } catch {
        reject(new ApiError('server_error', 'Unexpected response from the server.', xhr.status));
        return;
      }
      if (xhr.status >= 200 && xhr.status < 300 && body.reportId) {
        resolve(body as ResubmitResult);
      } else {
        reject(new ApiError(body.error?.code ?? 'server_error', body.error?.message ?? 'Resubmission failed.', xhr.status));
      }
    };
    xhr.onerror = () =>
      reject(new ApiError('network', 'The upload didn’t go through. Check your connection and try again.', 0));
    xhr.send(form);
  });
}
